import { useQuery } from '@apollo/client';
import { Partner, Program } from '@bybeam/platform-types';
import Loading from '@platform-ui-common/components/Loading/index.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import React, { ReactNode, useContext } from 'react';
import usePartner from '../usePartner.js';
import GetPartnerProgramsQuery from './GetPartnerProgramsQuery.graphql';

export interface PartnerProgramsContextValue {
  programs: Program[];
  loading: boolean;
  error?: Error;
}

// Only exported for use in tests/mocks; do NOT reference directly in prod code
export const PartnerProgramsContext = React.createContext<PartnerProgramsContextValue>({
  programs: [],
  loading: true,
});

export function PartnerProgramsProvider({ children }: { children: ReactNode }): JSX.Element {
  const partner = usePartner();
  const { loading: authLoading, user } = useAuth();

  // Skip query until auth is ready and we have a valid authenticated user
  const shouldFetch = !authLoading && !!user && !!partner?.externalId;

  const { data, loading, error } = useQuery<
    { partners: { partners: Partner[] } },
    { externalId: string }
  >(GetPartnerProgramsQuery, {
    variables: { externalId: partner.externalId },
    skip: !shouldFetch,
  });

  const programs = data?.partners?.partners?.[0]?.programs ?? [];
  const isLoading = authLoading || (shouldFetch && loading);

  return (
    <PartnerProgramsContext.Provider
      value={{
        programs,
        loading: isLoading,
        error: error ? new Error(error.message) : undefined,
      }}
    >
      {/* Adding a loading state is temporary until we have a better way to handle this */}
      {loading ? <Loading size="XXL" fullPage /> : children}
    </PartnerProgramsContext.Provider>
  );
}

export default function usePrograms(): PartnerProgramsContextValue {
  return useContext(PartnerProgramsContext);
}
